import {
  ComponentPropsWithoutRef,
  createContext,
  forwardRef,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil, useMergeRefs } from "../../hooks";
import { ToolbarButton } from "./components/ToolbarButton/ToolbarButton";
import { ToolbarButtonToggle } from "./components/ToolbarButtonToggle/ToolbarButtonToggle";
import { ToolbarButtonLink } from "./components/ToolbarButtonLink/ToolbarButtonLink";
import { ToolbarSelect } from "./components/ToolbarSelect/ToolbarSelect";
import { ToolbarOverflowMenu } from "./components/ToolbarOverflowMenu/ToolbarOverflowMenu";
import { ToolbarItemProps, ToolbarOverflowTypes } from "./sharedTypes";
import {
  handleToolbarKeyDown,
  updateToolbarItemsTabIndex,
} from "./utils/accessibility";
import { Flex } from "../Flex";

import cx from "classnames";
import styles from "./Toolbar.module.scss";

/**
 * Context for sharing toolbar state with child components for overflow handling
 */
export type ToolbarOverFlowContextProps = {
  /**
   * Array of toolbar items that should appear in the overflow menu
   */
  overflowItems: ToolbarItemProps[];
  /**
   * Function to add an item to the overflow menu
   */
  addItem: (element: ToolbarItemProps) => void;
  /**
   * Function to remove an item from the overflow menu
   */
  removeItem: (element: ToolbarItemProps) => void;
  /**
   * Reference to the toolbar container element
   */
  toolbarRef: RefObject<HTMLDivElement>;
  /**
   * Array of item IDs in their original order
   */
  orderedIds?: string[];
  /**
   * Overflow behavior type (wrap or collapse)
   */
  overflow: ToolbarOverflowTypes;
};

/**
 * Context for managing toolbar overflow state and operations
 */
export const ToolbarOverflowContext =
  createContext<ToolbarOverFlowContextProps>({
    overflowItems: [],
    addItem: () => {
      return;
    },
    removeItem: () => {
      return;
    },
    toolbarRef: { current: null },
    orderedIds: [],
    overflow: "wrap",
  });

/**
 * Props for the Toolbar component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type ToolbarProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /**
     * Description of the content this toolbar is associated with, used for accessibility
     */
    associatedContent: string;
    /**
     * Orientation of the toolbar
     * @default horizontal
     */
    direction?: "vertical" | "horizontal";
    /**
     * How to handle overflow when items don't fit
     * @default wrap
     */
    overflow?: ToolbarOverflowTypes;
    /**
     * Additional items to display in the overflow menu
     */
    additionalItems?: ReactElement[];
  };

const ToolbarElement = forwardRef<HTMLDivElement, ToolbarProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      associatedContent,
      className,
      children,
      style,
      direction = "horizontal",
      overflow = "wrap",
      onKeyDown,
      additionalItems,
      ...rest
    } = componentProps;

    // State for tracking items that should appear in the overflow menu
    const [overflowItems, setOverflowItems] = useState<ToolbarItemProps[]>([]);
    const toolbarItemsRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);

    // Track original order of items to maintain consistent ordering in the overflow menu
    const [orderedIds, setOrderedIds] = useState<string[]>([]);

    // Update ordered IDs when children change to maintain correct overflow order
    useEffect(() => {
      const newOrderedIds = Array.from(
        toolbarItemsRef.current?.querySelectorAll("[data-id]") || [],
      ).map((el) => {
        return el.getAttribute("data-id") as string;
      });

      setOrderedIds(newOrderedIds);
    }, [children]);

    // Add item to overflow menu
    const addItem = useCallback((item: ToolbarItemProps) => {
      setOverflowItems((prev) => [...prev, item]);
    }, []);

    // Remove item from overflow menu
    const removeItem = useCallback((item: ToolbarItemProps) => {
      setOverflowItems((prev) =>
        prev.filter((component) => component.id !== item.id),
      );
    }, []);

    // Initialize tabindex values
    useEffect(() => {
      if (toolbarRef.current) {
        // Initialize toolbar with the first item active
        updateToolbarItemsTabIndex(toolbarRef.current);
      }
    }, [children]);

    // Handle keyboard navigation events within the toolbar
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (toolbarRef.current) {
          handleToolbarKeyDown(event, toolbarRef.current);
        }

        onKeyDown?.(event);
      },
      [onKeyDown],
    );

    // Determine if overflow menu should be shown
    const showOverflowMenu =
      (overflow === "collapse" && overflowItems.length > 0) ||
      !!additionalItems;

    // Context value provided to all toolbar children
    const value = useMemo(
      () => ({
        overflowItems,
        addItem,
        removeItem,
        toolbarRef,
        orderedIds,
        overflow,
      }),
      [overflowItems, addItem, removeItem, toolbarRef, orderedIds, overflow],
    );

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    const toolbarClassNames = cx(styles["toolbar"], className);

    const toolbarContentClassNames = cx(styles["toolbar-content"], {
      [styles[`overflow-${overflow}`]]: overflow,
    });

    return (
      <ToolbarOverflowContext.Provider value={value}>
        <Flex
          direction={direction === "horizontal" ? "row" : "column"}
          wrap={overflow === "wrap" ? "wrap" : "nowrap"}
          alignItems="center"
          className={toolbarClassNames}
          style={styleCombined}
          ref={useMergeRefs([toolbarRef, ref])}
          role="toolbar"
          data-anv="toolbar"
          aria-orientation={direction}
          aria-label={`Toolbar for ${associatedContent}. Use arrow keys to navigate through toolbar controls.`}
          onKeyDown={handleKeyDown}
          {...rest}
        >
          <Flex
            ref={useMergeRefs([toolbarItemsRef, ref])}
            className={toolbarContentClassNames}
            direction={direction === "horizontal" ? "row" : "column"}
            wrap={overflow === "wrap" ? "wrap" : "nowrap"}
            alignItems="center"
            grow={1}
            data-anv="toolbar-content"
          >
            {children}
          </Flex>

          {showOverflowMenu && (
            <ToolbarOverflowMenu
              direction={direction}
              additionalItems={additionalItems}
            />
          )}
        </Flex>
      </ToolbarOverflowContext.Provider>
    );
  },
);

ToolbarElement.displayName = "Toolbar";

/**
 * Toolbar component for grouping related interactive elements.
 *
 * Features:
 * - Horizontal or vertical orientation
 * - Overflow handling with wrap or collapse behavior
 * - Keyboard navigation following ARIA best practices
 * - Screen reader announcements and accessibility
 * - Automatic overflow menu for collapsed items
 * - Support for buttons, toggles, selects, and links
 * - Layout utilities for positioning and spacing
 * - Context-based state management for child components
 *
 * @example
 * <Toolbar associatedContent="document editor" overflow="collapse">
 *   <Toolbar.Button icon={BoldIcon} aria-label="Bold" />
 *   <Toolbar.ButtonToggle icon={ItalicIcon} aria-label="Italic" />
 *   <Toolbar.Select
 *     items={fontOptions}
 *     selected="arial"
 *     accessibleLabel="Font family"
 *   />
 * </Toolbar>
 */
export const Toolbar = Object.assign(ToolbarElement, {
  /**
   * ToolbarButton component for standard clickable actions within a toolbar.
   *
   * Features:
   * - Designed specifically for toolbar integration
   * - Consistent styling with other toolbar items
   * - Automatic tooltip for icon-only buttons
   * - Overflow menu support
   * - Accessibility enforcement for aria-labels
   * - Small size optimized for toolbar layout
   * - Ghost and primary appearance options
   *
   * @example
   * <Toolbar.Button icon={BoldIcon} aria-label="Bold text" />
   *
   * @example
   * <Toolbar.Button appearance="primary">
   *   Save
   * </Toolbar.Button>
   */
  Button: ToolbarButton,
  /**
   * ToolbarButtonToggle component for toggleable actions within a toolbar.
   *
   * Features:
   * - Toggle state management (on/off)
   * - Designed specifically for toolbar integration
   * - Consistent styling with other toolbar items
   * - Automatic tooltip for icon-only toggles
   * - Overflow menu support
   * - Accessibility enforcement for aria-labels
   * - Small size optimized for toolbar layout
   * - Visual feedback for toggle state
   * - Proper ARIA attributes for toggle semantics
   *
   * @example
   * <Toolbar.ButtonToggle icon={BoldIcon} aria-label="Bold" checked={isBold} />
   *
   * @example
   * <Toolbar.ButtonToggle checked={isItalic}>
   *   Italic
   * </Toolbar.ButtonToggle>
   */
  ButtonToggle: ToolbarButtonToggle,
  /**
   * ToolbarButtonLink component for link actions within a toolbar.
   *
   * Features:
   * - Link functionality with href support
   * - Designed specifically for toolbar integration
   * - Consistent styling with other toolbar items
   * - Automatic tooltip for icon-only links
   * - Overflow menu support
   * - Accessibility enforcement for aria-labels
   * - Small size optimized for toolbar layout
   * - Ghost and primary appearance options
   * - Proper anchor element semantics
   *
   * @example
   * <Toolbar.ButtonLink href="/help">
   *   Help
   * </Toolbar.ButtonLink>
   *
   * @example
   * <Toolbar.ButtonLink icon={ExternalLinkIcon} href="/docs" aria-label="Documentation" />
   */
  ButtonLink: ToolbarButtonLink,
  /**
   * ToolbarSelect component provides a dropdown selector within a toolbar.
   *
   * Features:
   * - Dropdown menu with selectable options
   * - Controlled and uncontrolled usage patterns
   * - Automatic overflow menu integration
   * - Accessibility support with proper ARIA labels
   * - Small size optimized for toolbar layout
   * - Ghost and primary appearance options
   * - Dropdown arrow icon indicator
   * - Listbox integration for keyboard navigation
   *
   * @example
   * <Toolbar.Select
   *   items={fontOptions}
   *   selected="arial"
   *   accessibleLabel="Font family"
   *   onChange={(id) => setFont(id)}
   * />
   *
   * @example
   * <Toolbar.Select
   *   appearance="primary"
   *   items={colorOptions}
   *   accessibleLabel="Text color"
   * />
   */
  Select: ToolbarSelect,
});
